import {
  ACESFilmicToneMapping, AdditiveBlending, AmbientLight, Box3, BufferAttribute,
  BufferGeometry, Color, CylinderGeometry, DirectionalLight, FogExp2, Group,
  Mesh, MeshBasicMaterial, MeshStandardMaterial, Object3D, PerspectiveCamera,
  PointLight, Points, PointsMaterial, Raycaster, Scene, Sprite,
  SpriteMaterial, SRGBColorSpace, TextureLoader, TorusGeometry, Vector2, Vector3,
  WebGLRenderer,
} from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";
import { DRACOLoader } from "three/addons/loaders/DRACOLoader.js";
import { GLTFLoader } from "three/addons/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";

type PreviewItem = {
  id: string; shortname: string; label: string; quantity: number; container: string;
  position: number; skin: number; condition: number; maxCondition: number; ammo: number;
  ammoType: string; contents: unknown[]; kitName: string; iconUrl: string; modelUrl: string;
  modelScale: number; category: string;
};
type Payload = { version: number; title: string; items: PreviewItem[]; decoderPath: string; labels: Record<string, string> };
type Quality = "low" | "medium" | "high";
type LayoutState = { position: Vector3; rotation: Vector3; scale: Vector3 };

const record = (type: string, details: Record<string, unknown> = {}) => {
  const diagnostic = (window as Window & { __raidlandsRecordAnimationDiagnostic?: (type: string, data: unknown) => void }).__raidlandsRecordAnimationDiagnostic;
  diagnostic?.(`store_preview_${type}`, details);
};

function supportsWebGL2(): boolean {
  try { return Boolean(document.createElement("canvas").getContext("webgl2")); } catch { return false; }
}

function hidePreview(host: HTMLElement, reason: string) {
  host.hidden = true;
  const section = host.closest<HTMLElement>(".store-preview-section");
  if (section) section.hidden = true;
  record("unavailable", { reason });
}

function parsePayload(host: HTMLElement): Payload | null {
  const node = host.querySelector<HTMLScriptElement>("[data-store-preview-payload]");
  if (!node?.textContent) return null;
  try { return JSON.parse(node.textContent) as Payload; } catch { return null; }
}

function deviceQuality(): Quality {
  const nav = navigator as Navigator & { deviceMemory?: number; connection?: { saveData?: boolean } };
  const memory = Number(nav.deviceMemory || 0);
  const cores = Number(nav.hardwareConcurrency || 0);
  const coarse = window.matchMedia?.("(pointer: coarse)").matches || false;
  if (nav.connection?.saveData || (memory > 0 && memory < 4) || (cores > 0 && cores <= 4)) return "low";
  if (!coarse && memory >= 8 && cores >= 8) return "high";
  return "medium";
}

class StorePreview {
  private scene = new Scene();
  private camera = new PerspectiveCamera(38, 1, 0.05, 120);
  private renderer: WebGLRenderer;
  private controls: OrbitControls;
  private stage = new Group();
  private effects = new Group();
  private raycaster = new Raycaster();
  private pointer = new Vector2();
  private pickables: Group[] = [];
  private layout = new Map<Group, LayoutState>();
  private modelCache = new Map<string, Object3D>();
  private selected: Group | null = null;
  private hovered: Group | null = null;
  private focusRing: Mesh;
  private particles: Points | null = null;
  private smoke: Sprite[] = [];
  private frame = 0;
  private disposed = false;
  private resizeObserver: ResizeObserver;
  private gltf: GLTFLoader;
  private textureLoader = new TextureLoader();
  private quality = deviceQuality();
  private started = performance.now();
  private lastFrameAt = performance.now();
  private performanceWindowAt = performance.now();
  private performanceFrames = 0;
  private degraded = false;
  private reducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches || false;
  private cameraFrom = new Vector3();
  private cameraTo = new Vector3();
  private cameraTweenStarted = 0;
  private cameraTweenDuration = 0;

  constructor(private host: HTMLElement, private payload: Payload) {
    const canvasHost = host.querySelector<HTMLElement>("[data-store-preview-canvas]");
    if (!canvasHost) throw new Error("missing-canvas-host");
    this.renderer = new WebGLRenderer({ antialias: this.quality !== "low", alpha: true, powerPreference: "high-performance" });
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, this.quality === "high" ? 1.75 : this.quality === "medium" ? 1.35 : 1));
    this.renderer.outputColorSpace = SRGBColorSpace;
    this.renderer.toneMapping = ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 1.12;
    this.renderer.domElement.setAttribute("aria-label", `${payload.title} interactive 3D kit preview`);
    this.renderer.domElement.tabIndex = 0;
    canvasHost.replaceChildren(this.renderer.domElement);

    this.camera.position.set(0, 6.7, 12.8);
    this.controls = new OrbitControls(this.camera, this.renderer.domElement);
    this.controls.enableDamping = !this.reducedMotion;
    this.controls.dampingFactor = 0.07;
    this.controls.minDistance = 4.2;
    this.controls.maxDistance = 18;
    this.controls.maxPolarAngle = Math.PI * 0.49;
    this.controls.target.set(0, 0.9, 0);

    const draco = new DRACOLoader();
    draco.setDecoderPath(payload.decoderPath);
    this.gltf = new GLTFLoader();
    this.gltf.setDRACOLoader(draco);
    this.gltf.setMeshoptDecoder(MeshoptDecoder);

    this.focusRing = new Mesh(
      new TorusGeometry(1.7, 0.035, 8, 96),
      new MeshBasicMaterial({ color: 0x56f0a4, transparent: true, opacity: 0.9, blending: AdditiveBlending }),
    );
    this.focusRing.rotation.x = Math.PI / 2;
    this.focusRing.position.y = 0.52;
    this.focusRing.visible = false;
    this.buildEnvironment();
    this.bind();
    this.resizeObserver = new ResizeObserver(() => this.resize());
    this.resizeObserver.observe(canvasHost);
    this.resize();
  }

  private buildEnvironment() {
    this.scene.background = new Color(0x05090b);
    this.scene.fog = new FogExp2(0x07100f, this.quality === "low" ? 0.025 : 0.04);
    this.scene.add(new AmbientLight(0x91aaa7, this.quality === "low" ? 1.15 : 0.75));
    const key = new DirectionalLight(0xffca91, this.quality === "high" ? 5.2 : 4.2); key.position.set(5, 10, 7); this.scene.add(key);
    const rim = new DirectionalLight(0x35e696, 3.2); rim.position.set(-7, 6, -6); this.scene.add(rim);
    const fill = new PointLight(0x3a9fff, this.quality === "high" ? 55 : 32, 16, 2); fill.position.set(5, 2.5, -2); this.scene.add(fill);
    if (this.quality !== "low") {
      const ember = new PointLight(0xff7133, this.quality === "high" ? 60 : 34, 15, 2); ember.position.set(-4, 1.5, 3); this.scene.add(ember);
    }

    const floor = new Mesh(new CylinderGeometry(6.4, 6.75, 0.28, 72), new MeshStandardMaterial({ color: 0x12191a, metalness: 0.84, roughness: 0.34 }));
    floor.position.y = -0.16; this.scene.add(floor);
    const pedestal = new Mesh(new CylinderGeometry(1.55, 1.92, 0.62, 12), new MeshStandardMaterial({ color: 0x242e2e, metalness: 0.92, roughness: 0.24, emissive: 0x08291b, emissiveIntensity: 1.1 }));
    pedestal.position.y = 0.24; this.scene.add(pedestal);
    for (let ring = 0; ring < (this.quality === "high" ? 3 : 2); ring += 1) {
      const glow = new Mesh(new TorusGeometry(2.35 + ring * 1.45, 0.012 + ring * 0.005, 6, 100), new MeshBasicMaterial({ color: ring % 2 ? 0xff7438 : 0x36e292, transparent: true, opacity: 0.28, blending: AdditiveBlending }));
      glow.rotation.x = Math.PI / 2; glow.position.y = 0.01 + ring * 0.02; this.effects.add(glow);
    }
    this.effects.add(this.focusRing);
    this.createParticles();
    this.createSmoke();
    this.scene.add(this.stage, this.effects);
  }

  private createParticles() {
    const count = this.quality === "high" ? 180 : this.quality === "medium" ? 85 : 35;
    const positions = new Float32Array(count * 3);
    for (let index = 0; index < count; index += 1) {
      const radius = 3 + Math.random() * 6;
      const angle = Math.random() * Math.PI * 2;
      positions[index * 3] = Math.cos(angle) * radius;
      positions[index * 3 + 1] = 0.25 + Math.random() * 5.8;
      positions[index * 3 + 2] = Math.sin(angle) * radius;
    }
    const geometry = new BufferGeometry(); geometry.setAttribute("position", new BufferAttribute(positions, 3));
    this.particles = new Points(geometry, new PointsMaterial({ color: 0xff9b4f, size: this.quality === "low" ? 0.035 : 0.055, transparent: true, opacity: 0.72, blending: AdditiveBlending, depthWrite: false }));
    this.effects.add(this.particles);
  }

  private createSmoke() {
    if (this.quality === "low") return;
    const canvas = document.createElement("canvas"); canvas.width = 128; canvas.height = 128;
    const context = canvas.getContext("2d"); if (!context) return;
    const gradient = context.createRadialGradient(64, 64, 4, 64, 64, 62);
    gradient.addColorStop(0, "rgba(125,170,155,.34)"); gradient.addColorStop(0.45, "rgba(45,72,66,.16)"); gradient.addColorStop(1, "rgba(6,12,12,0)");
    context.fillStyle = gradient; context.fillRect(0, 0, 128, 128);
    const texture = new TextureLoader().load(canvas.toDataURL()); texture.colorSpace = SRGBColorSpace;
    const count = this.quality === "high" ? 8 : 4;
    for (let index = 0; index < count; index += 1) {
      const smoke = new Sprite(new SpriteMaterial({ map: texture, transparent: true, depthWrite: false, opacity: 0.5, color: index % 2 ? 0x589c80 : 0x6d7774 }));
      const angle = (index / count) * Math.PI * 2; smoke.position.set(Math.cos(angle) * 3.4, 0.45 + (index % 2) * 0.3, Math.sin(angle) * 3.4); smoke.scale.setScalar(2.2 + (index % 3) * 0.7);
      smoke.userData.phase = Math.random() * Math.PI * 2; this.smoke.push(smoke); this.effects.add(smoke);
    }
  }

  private bind() {
    const canvas = this.renderer.domElement;
    canvas.addEventListener("pointermove", this.onPointerMove);
    canvas.addEventListener("pointerdown", () => { if (this.selected) this.controls.autoRotate = false; });
    canvas.addEventListener("click", this.onClick);
    canvas.addEventListener("keydown", this.onKeyDown);
    canvas.addEventListener("webglcontextlost", this.onContextLost);
    this.host.querySelector("[data-store-preview-close]")?.addEventListener("click", () => this.leaveFocus());
  }

  async start() {
    await this.setItems(this.payload.items);
    this.host.dataset.previewState = "ready";
    this.setStatus("Interactive kit preview ready. Choose an item to inspect it.");
    record("ready", { durationMs: Math.round(performance.now() - this.started), items: this.pickables.length, quality: this.quality });
    this.animate();
  }

  private categorySlots(items: PreviewItem[]): Map<string, { index: number; total: number }> {
    const totals = new Map<string, number>();
    items.forEach((item) => totals.set(item.category, (totals.get(item.category) || 0) + 1));
    const seen = new Map<string, number>(); const slots = new Map<string, { index: number; total: number }>();
    items.forEach((item) => { const index = seen.get(item.category) || 0; seen.set(item.category, index + 1); slots.set(item.id, { index, total: totals.get(item.category) || 1 }); });
    return slots;
  }

  private positionFor(item: PreviewItem, index: number, total: number): Vector3 {
    const spread = (slot: number, count: number, width: number) => (slot - (count - 1) / 2) * width;
    if (item.category === "armor") return new Vector3(spread(index, total, 1.25), 0.48, -3.55 - Math.floor(index / 9) * 1.15);
    if (item.category === "weapon") return new Vector3(index % 2 ? 4.2 : -4.2, 0.58, -2.25 + Math.floor(index / 2) * 1.65);
    if (item.category === "vehicle") {
      if (total === 1) return new Vector3(0, 0.42, 0);
      const angle = (index / total) * Math.PI * 2 - Math.PI / 2;
      const radius = total <= 4 ? 3.2 : 4.25;
      return new Vector3(Math.cos(angle) * radius, 0.42, Math.sin(angle) * radius);
    }
    if (item.category === "sentry" || item.category === "deployable") return new Vector3(index % 2 ? 4.55 : -4.55, 0.42, -1.8 + Math.floor(index / 2) * 2.1);
    const columns = Math.min(8, total); const row = Math.floor(index / columns); const column = index % columns;
    const zBase = item.category === "ammo" || item.category === "explosive" ? 3.6 : 2.35;
    return new Vector3(spread(column, Math.min(columns, total - row * columns), 1.35), 0.5, zBase + row * 1.3);
  }

  private async setItems(items: PreviewItem[]) {
    const slots = this.categorySlots(items);
    const order: Record<string, number> = { vehicle: 0, armor: 1, sentry: 2, deployable: 3, weapon: 4, attachment: 5, medical: 6, item: 7, resource: 8, explosive: 9, ammo: 10 };
    const sorted = [...items].sort((left, right) => (order[left.category] ?? 6) - (order[right.category] ?? 6) || left.position - right.position);
    for (const item of sorted) {
      const slot = slots.get(item.id) || { index: 0, total: 1 };
      const root = new Group();
      root.position.copy(sorted.length === 1 ? new Vector3(0, 0.58, 0) : this.positionFor(item, slot.index, slot.total));
      root.userData.item = item;
      const visual = await this.loadVisual(item); root.add(visual);
      if (item.category === "sentry" && item.modelUrl && item.quantity > 1) {
        const placedCount = Math.min(item.quantity, 8);
        const columns = Math.min(4, placedCount);
        const placeSentry = (object: Object3D, placed: number) => {
          const row = Math.floor(placed / columns); const column = placed % columns;
          const rowCount = Math.min(columns, placedCount - row * columns);
          object.position.x += (column - (rowCount - 1) / 2) * 0.72;
          object.position.z += (row - (Math.ceil(placedCount / columns) - 1) / 2) * 0.72;
        };
        placeSentry(visual, 0);
        for (let placed = 1; placed < placedCount; placed += 1) {
          const extra = await this.loadVisual(item); placeSentry(extra, placed); root.add(extra);
        }
      }
      this.layout.set(root, { position: root.position.clone(), rotation: new Vector3(root.rotation.x, root.rotation.y, root.rotation.z), scale: root.scale.clone() });
      this.stage.add(root); this.pickables.push(root);
    }
  }

  private async loadVisual(item: PreviewItem): Promise<Object3D> {
    if (item.modelUrl) {
      try {
        let source = this.modelCache.get(item.modelUrl);
        if (!source) { source = (await this.gltf.loadAsync(item.modelUrl)).scene; this.modelCache.set(item.modelUrl, source); }
        const model = source.clone(true); const box = new Box3().setFromObject(model); const size = box.getSize(new Vector3());
        const target = item.category === "vehicle" ? 2.3 : item.category === "sentry" || item.category === "deployable" ? 1.65 : item.category === "weapon" ? 1.5 : 1.15;
        model.scale.setScalar((target / Math.max(size.x, size.y, size.z, 0.01)) * item.modelScale);
        const fitted = new Box3().setFromObject(model); const center = fitted.getCenter(new Vector3()); model.position.sub(center); model.position.y -= fitted.min.y;
        model.traverse((child) => { child.userData.item = item; });
        return model;
      } catch { record("item_model_fallback", { shortname: item.shortname }); }
    }
    const card = new Group();
    let hasIcon = false;
    if (item.iconUrl) {
      try {
        const texture = await this.textureLoader.loadAsync(item.iconUrl);
        texture.colorSpace = SRGBColorSpace;
        const icon = new Sprite(new SpriteMaterial({ map: texture, transparent: true, depthWrite: false, alphaTest: 0.04 }));
        icon.scale.set(1.14, 1.14, 1);
        card.add(icon);
        hasIcon = true;
      } catch { /* use the neutral marker below */ }
    }
    if (!hasIcon) {
      const marker = new Mesh(
        new TorusGeometry(0.38, 0.045, 12, 36),
        new MeshBasicMaterial({ color: 0x56f5a7, transparent: true, opacity: 0.76 }),
      );
      marker.userData.item = item;
      card.add(marker);
    }
    card.position.y = 0.72; card.userData.item = item; return card;
  }

  private rootFor(object: Object3D | null): Group | null {
    let current = object;
    while (current && current.parent !== this.stage && current !== this.selected) current = current.parent;
    return current instanceof Group && current.userData.item ? current : null;
  }

  private pick(event: MouseEvent | PointerEvent): Group | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    this.pointer.set(((event.clientX - rect.left) / rect.width) * 2 - 1, -((event.clientY - rect.top) / rect.height) * 2 + 1);
    this.raycaster.setFromCamera(this.pointer, this.camera);
    return this.rootFor(this.raycaster.intersectObjects(this.pickables, true)[0]?.object || null);
  }

  private setHovered(root: Group | null) {
    if (this.selected) return;
    if (this.hovered && this.hovered !== root) this.hovered.scale.copy(this.layout.get(this.hovered)?.scale || new Vector3(1, 1, 1));
    this.hovered = root;
    if (!root) { this.renderer.domElement.style.cursor = "grab"; this.updateHud("Explore the kit", "Hover an item for its quantity. Click to inspect."); return; }
    root.scale.copy(this.layout.get(root)?.scale || new Vector3(1, 1, 1)).multiplyScalar(1.1);
    this.renderer.domElement.style.cursor = "pointer";
    const item = root.userData.item as PreviewItem;
    this.updateHud(item.label, `${item.quantity.toLocaleString()}x in ${item.container} - click to inspect`);
    this.setStatus(`${item.label}, quantity ${item.quantity.toLocaleString()}`);
  }

  private focus(root: Group) {
    if (this.selected === root) return;
    if (this.selected) this.leaveFocus(false);
    this.selected = root; this.hovered = null;
    this.pickables.forEach((candidate) => { candidate.visible = candidate === root; });
    root.visible = true; root.position.set(0, 0.58, 0); root.rotation.set(0, 0, 0); root.scale.setScalar(1.65);
    this.focusRing.visible = true;
    this.controls.target.set(0, 1.25, 0); this.controls.minDistance = 3.25; this.controls.maxDistance = 7;
    this.controls.autoRotate = !this.reducedMotion; this.controls.autoRotateSpeed = 1.65;
    this.moveCamera(new Vector3(0, 2.15, 4.8), 650);
    const item = root.userData.item as PreviewItem; this.updateHud(item.label, `${item.quantity.toLocaleString()}x - drag to inspect from any direction`); this.openDrawer(item);
  }

  private leaveFocus(focusCanvas = true) {
    if (!this.selected) return;
    const state = this.layout.get(this.selected);
    if (state) { this.selected.position.copy(state.position); this.selected.rotation.set(state.rotation.x, state.rotation.y, state.rotation.z); this.selected.scale.copy(state.scale); }
    this.pickables.forEach((candidate) => { candidate.visible = true; });
    this.selected = null; this.focusRing.visible = false; this.controls.autoRotate = false;
    this.controls.target.set(0, 0.9, 0); this.controls.minDistance = 4.2; this.controls.maxDistance = 18;
    this.moveCamera(new Vector3(0, 6.7, 12.8), 650); this.closeDrawer(); this.updateHud("Explore the kit", "Hover an item for its quantity. Click to inspect.");
    if (focusCanvas) this.renderer.domElement.focus();
  }

  private moveCamera(target: Vector3, duration: number) {
    if (this.reducedMotion) { this.camera.position.copy(target); return; }
    this.cameraFrom.copy(this.camera.position); this.cameraTo.copy(target); this.cameraTweenStarted = performance.now(); this.cameraTweenDuration = duration;
  }

  private onPointerMove = (event: PointerEvent) => this.setHovered(this.pick(event));
  private onClick = (event: MouseEvent) => { const root = this.pick(event); if (root) this.focus(root); };
  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") { this.leaveFocus(); return; }
    if ((event.key === "Enter" || event.key === " ") && this.pickables.length) { event.preventDefault(); this.focus(this.hovered || this.pickables[0]); }
  };
  private onContextLost = (event: Event) => { event.preventDefault(); this.fail("context-lost"); };

  private openDrawer(item: PreviewItem) {
    const drawer = this.host.querySelector<HTMLElement>("[data-store-preview-drawer]"); if (!drawer) return;
    drawer.querySelector<HTMLElement>("[data-store-preview-detail-title]")!.textContent = item.label;
    drawer.querySelector<HTMLElement>("[data-store-preview-detail-meta]")!.textContent = `${item.quantity.toLocaleString()}x - ${item.container} - ${item.kitName}`;
    drawer.querySelector<HTMLElement>("[data-store-preview-detail-extra]")!.textContent = [item.ammo ? `${item.ammo} ammo${item.ammoType ? ` / ${item.ammoType}` : ""}` : "", item.skin ? `Skin ${item.skin}` : "", item.maxCondition ? `Condition ${item.condition}/${item.maxCondition}` : ""].filter(Boolean).join(" - ") || item.shortname;
    const image = drawer.querySelector<HTMLImageElement>("[data-store-preview-detail-image]"); if (image) { image.src = item.iconUrl; image.hidden = !item.iconUrl; }
    drawer.hidden = false;
  }
  private closeDrawer() { const drawer = this.host.querySelector<HTMLElement>("[data-store-preview-drawer]"); if (drawer) drawer.hidden = true; }
  private updateHud(title: string, meta: string) { const titleNode = this.host.querySelector<HTMLElement>("[data-store-preview-hud-title]"); const metaNode = this.host.querySelector<HTMLElement>("[data-store-preview-hud-meta]"); if (titleNode) titleNode.textContent = title; if (metaNode) metaNode.textContent = meta; }
  private setStatus(message: string) { const status = this.host.querySelector<HTMLElement>("[data-store-preview-status]"); if (status) status.textContent = message; }
  private resize() { const parent = this.renderer.domElement.parentElement; if (!parent) return; const width = Math.max(1, parent.clientWidth); const height = Math.max(360, parent.clientHeight); this.renderer.setSize(width, height, false); this.camera.aspect = width / height; this.camera.updateProjectionMatrix(); }

  private degradeEffects() {
    if (this.degraded) return; this.degraded = true; this.smoke.forEach((sprite, index) => { sprite.visible = index < 2; });
    if (this.particles) this.particles.visible = this.quality !== "low";
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 1)); record("quality_reduced", { reason: "sustained-low-fps" });
  }

  private animate = () => {
    if (this.disposed) return;
    try {
      const now = performance.now(); const delta = Math.min(40, now - this.lastFrameAt); this.lastFrameAt = now;
      if (this.cameraTweenDuration > 0) { const progress = Math.min(1, (now - this.cameraTweenStarted) / this.cameraTweenDuration); const eased = 1 - Math.pow(1 - progress, 3); this.camera.position.lerpVectors(this.cameraFrom, this.cameraTo, eased); if (progress >= 1) this.cameraTweenDuration = 0; }
      if (this.particles) this.particles.rotation.y += delta * 0.000025;
      this.smoke.forEach((sprite, index) => { sprite.position.y += Math.sin(now * 0.00025 + Number(sprite.userData.phase)) * 0.0008; sprite.material.rotation += (index % 2 ? 1 : -1) * delta * 0.000015; });
      this.focusRing.rotation.z += delta * 0.00035;
      this.controls.update(); this.renderer.render(this.scene, this.camera);
      this.performanceFrames += 1;
      if (now - this.performanceWindowAt >= 2500) { const fps = this.performanceFrames * 1000 / (now - this.performanceWindowAt); if (fps < 35) this.degradeEffects(); this.performanceWindowAt = now; this.performanceFrames = 0; }
      this.frame = requestAnimationFrame(this.animate);
    } catch { this.fail("render-error"); }
  };

  fail(reason: string) { this.dispose(); hidePreview(this.host, reason); }
  dispose() { if (this.disposed) return; this.disposed = true; cancelAnimationFrame(this.frame); this.resizeObserver?.disconnect(); this.controls.dispose(); this.renderer.dispose(); this.renderer.domElement.remove(); }
}

function initialize(host: HTMLElement) {
  const payload = parsePayload(host); if (!payload) { hidePreview(host, "invalid-payload"); return; }
  record("attempted", { version: payload.version });
  if (!supportsWebGL2()) { hidePreview(host, "no-webgl2"); return; }
  let preview: StorePreview | null = null;
  const timeout = window.setTimeout(() => { if (host.dataset.previewState === "loading") preview?.fail("startup-timeout"); }, 12000);
  try { preview = new StorePreview(host, payload); void preview.start().catch(() => preview?.fail("load-error")).finally(() => window.clearTimeout(timeout)); }
  catch { window.clearTimeout(timeout); hidePreview(host, "initialization-error"); }
}

document.querySelectorAll<HTMLElement>("[data-store-preview]").forEach((host) => {
  if ("IntersectionObserver" in window) { const observer = new IntersectionObserver((entries) => { if (entries.some((entry) => entry.isIntersecting)) { observer.disconnect(); initialize(host); } }, { rootMargin: "300px" }); observer.observe(host); }
  else initialize(host);
});
